import type {
  CautionaryProfile,
  ReferenceProfile,
  StructuredRubricFields,
} from "@shared/labs/level-assessment-model";

/**
 * Pure cell/level helpers + the matrix data-model types, shared by the model
 * wizard (cross + bundled disciplines), the standalone discipline step of the
 * create→calibrate workflow, and the `<CompetencyMatrix>` component. Keeping
 * them in one place keeps the authoring journeys byte-for-byte consistent (same
 * shapes sent to the backend) and avoids a component↔helper import cycle — the
 * types live here (the data module) and the component imports them from here.
 */

export interface AxisDraft {
  label: string;
  description: string;
  /** Importance weight for the weighted aggregation. Must be > 0. Defaults to 1. */
  weight: number;
  // ── Structured pillars (Pillar 1/3/4) — all optional; a blob-only axis omits
  //    them and behaves exactly as before. Edited via `onUpdateAxis`. ──────────
  /** Pillar 1 · "what it is not". */
  notAbout?: string;
  /** Pillar 1 · baseline level (a level number, or null when unset). */
  baseline?: number | null;
  /** Pillar 3 · "what doesn't count" (prestige/title traps). */
  falsePositives?: string;
  /** Pillar 3 · "hard cap". */
  hardCap?: string;
  /** Pillar 4 · cautionary profiles. */
  cautionaryProfiles?: CautionaryProfile[];
}

/** Cell map: axis INDEX → level → description (Pillar 2 "def"). Indexed by
 *  position because axis ids are generated server-side from labels. */
export type CellMap = Record<number, Record<number, string>>;

/** Structured-fields map: axis INDEX → the per-cell evidence + reference
 *  profiles + per-adjacent-pair boundaries for that axis (Pillar 2 boundary,
 *  Pillar 3 evidence, Pillar 4 reference profiles). Parallel to `CellMap`;
 *  level keys are strings (matching the persisted `fields` JSONB). */
export type FieldsMap = Record<number, StructuredRubricFields>;

/** Inclusive `[min, max]` level ladder as an array. */
export function levelsArray(min: number, max: number): number[] {
  const out: number[] = [];
  for (let l = min; l <= max; l++) out.push(l);
  return out;
}

/** Build a position-indexed CellMap from an axisId-keyed cell map (edit mode). */
export function cellMapFromAxisCells(
  axes: { id: string }[],
  byAxisId: Record<string, Record<number, string>> | undefined,
): CellMap {
  const out: CellMap = {};
  axes.forEach((axis, i) => {
    out[i] = byAxisId?.[axis.id] ?? {};
  });
  return out;
}

/** Remove an axis and re-key the cells above it down by one position. */
export function removeAxisReindex(cells: CellMap, idx: number): CellMap {
  const next: CellMap = {};
  for (const [k, v] of Object.entries(cells)) {
    const i = Number(k);
    if (i === idx) continue;
    next[i < idx ? i : i - 1] = v;
  }
  return next;
}

export function setCellIn(cells: CellMap, axisIdx: number, level: number, value: string): CellMap {
  const next = { ...cells };
  const byLevel = { ...(next[axisIdx] ?? {}) };
  byLevel[level] = value;
  next[axisIdx] = byLevel;
  return next;
}

export function dropLevelFromCells(cells: CellMap, level: number): CellMap {
  const next: CellMap = {};
  for (const [k, byLevel] of Object.entries(cells)) {
    const filtered: Record<number, string> = {};
    for (const [lk, lv] of Object.entries(byLevel)) {
      if (Number(lk) !== level) filtered[Number(lk)] = lv;
    }
    next[Number(k)] = filtered;
  }
  return next;
}

export function countEmptyCells(axes: AxisDraft[], cells: CellMap, levels: number[]): number {
  let count = 0;
  for (let i = 0; i < axes.length; i++) {
    for (const level of levels) {
      if (!cells[i]?.[level]?.trim()) count += 1;
    }
  }
  return count;
}

/** Build the parallel `rubricCells` array the backend expects (one record per
 *  axis, keyed by level string, blanks dropped). */
export function buildRubricCells(
  axes: AxisDraft[],
  cells: CellMap,
  levels: number[],
): Record<string, string>[] {
  return axes.map((_, i) => {
    const byLevel: Record<string, string> = {};
    for (const level of levels) {
      const v = cells[i]?.[level];
      if (v && v.trim().length > 0) byLevel[String(level)] = v;
    }
    return byLevel;
  });
}

/** Build the create/update wire payload for one axis: identity + weight + any
 *  non-empty per-axis pillars (Pillar 1/3/4), omitted when blank to match the
 *  backend's optional shape. Shared by the model wizard + the discipline step so
 *  the two journeys send byte-identical axis payloads. */
export function buildAxisPayload(a: AxisDraft) {
  return {
    label: a.label.trim(),
    description: a.description.trim(),
    weight: a.weight,
    ...(a.notAbout?.trim() ? { notAbout: a.notAbout.trim() } : {}),
    ...(a.baseline != null ? { baseline: a.baseline } : {}),
    ...(a.falsePositives?.trim() ? { falsePositives: a.falsePositives.trim() } : {}),
    ...(a.hardCap?.trim() ? { hardCap: a.hardCap.trim() } : {}),
    ...(a.cautionaryProfiles?.length ? { cautionaryProfiles: a.cautionaryProfiles } : {}),
  };
}

// ───────────────────────────────────────────────────────────────────────────
// Structured fields (Pillar 2 boundary + Pillar 3 evidence + Pillar 4 reference
// profiles). Parallel to the def CellMap above; the per-axis pillars live on
// AxisDraft and are edited via onUpdateAxis, not here.
// ───────────────────────────────────────────────────────────────────────────

/** Build a position-indexed `FieldsMap` from an axisId-keyed map (edit mode),
 *  mirroring `cellMapFromAxisCells`. */
export function fieldsMapFromAxisFields(
  axes: { id: string }[],
  byAxisId: Record<string, StructuredRubricFields> | undefined,
): FieldsMap {
  const out: FieldsMap = {};
  axes.forEach((axis, i) => {
    out[i] = byAxisId?.[axis.id] ?? {};
  });
  return out;
}

/** Build the parallel `rubricFields` array the backend expects (one
 *  `StructuredRubricFields` per axis), pruned to the in-band `levels` — mirrors
 *  `buildRubricCells`, so out-of-band cells/boundaries are dropped at the source
 *  (prevents orphan JSONB entries). The backend further normalizes empties → null. */
export function buildRubricFields(
  axes: AxisDraft[],
  fields: FieldsMap,
  levels: number[],
): StructuredRubricFields[] {
  const inBand = new Set(levels.map(String));
  return axes.map((_, i) => {
    const f = fields[i] ?? {};
    const out: StructuredRubricFields = {};
    const cells: NonNullable<StructuredRubricFields["cells"]> = {};
    for (const [lvl, cell] of Object.entries(f.cells ?? {})) {
      if (inBand.has(lvl)) cells[lvl] = cell;
    }
    const boundaries: Record<string, string> = {};
    for (const [lvl, text] of Object.entries(f.boundaries ?? {})) {
      // A boundary is the `lvl → lvl+1` crossing — keep only when both ends are in band.
      if (inBand.has(lvl) && inBand.has(String(Number(lvl) + 1))) boundaries[lvl] = text;
    }
    if (Object.keys(cells).length) out.cells = cells;
    if (Object.keys(boundaries).length) out.boundaries = boundaries;
    return out;
  });
}

/** Reindex a `FieldsMap` after removing an axis (mirror of `removeAxisReindex`). */
export function removeAxisReindexFields(fields: FieldsMap, idx: number): FieldsMap {
  const next: FieldsMap = {};
  for (const [k, v] of Object.entries(fields)) {
    const i = Number(k);
    if (i === idx) continue;
    next[i < idx ? i : i - 1] = v;
  }
  return next;
}

/** Drop a level from every axis's structured fields: removes the level's cell
 *  and the two boundaries adjacent to it (the `level-1 → level` and
 *  `level → level+1` crossings, which no longer exist). */
export function dropLevelFromFields(fields: FieldsMap, level: number): FieldsMap {
  const lvl = String(level);
  const below = String(level - 1);
  const next: FieldsMap = {};
  for (const [k, f] of Object.entries(fields)) {
    const out: StructuredRubricFields = {};
    const cells: NonNullable<StructuredRubricFields["cells"]> = {};
    for (const [l, cell] of Object.entries(f.cells ?? {})) {
      if (l !== lvl) cells[l] = cell;
    }
    const boundaries: Record<string, string> = {};
    for (const [l, text] of Object.entries(f.boundaries ?? {})) {
      if (l !== lvl && l !== below) boundaries[l] = text;
    }
    if (Object.keys(cells).length) out.cells = cells;
    if (Object.keys(boundaries).length) out.boundaries = boundaries;
    next[Number(k)] = out;
  }
  return next;
}

// ── Single-axis immutable editors used by the matrix editor UI. Each returns a
//    new StructuredRubricFields for one axis (the UI then calls onSetFields). ──

/** Set a per-cell evidence field (`whatCounts` / `easyToMiss`) at one level. */
export function setStructuredCell(
  axisFields: StructuredRubricFields,
  level: number,
  key: "whatCounts" | "easyToMiss",
  value: string,
): StructuredRubricFields {
  const lvl = String(level);
  const cells = { ...(axisFields.cells ?? {}) };
  cells[lvl] = { ...(cells[lvl] ?? {}), [key]: value };
  return { ...axisFields, cells };
}

/** Set the "crosses-when" boundary for the `lowerLevel → lowerLevel+1` pair. */
export function setBoundaryIn(
  axisFields: StructuredRubricFields,
  lowerLevel: number,
  value: string,
): StructuredRubricFields {
  const boundaries = { ...(axisFields.boundaries ?? {}) };
  boundaries[String(lowerLevel)] = value;
  return { ...axisFields, boundaries };
}

/** Append a blank reference profile at a level. */
export function addReferenceProfileIn(
  axisFields: StructuredRubricFields,
  level: number,
): StructuredRubricFields {
  const lvl = String(level);
  const cells = { ...(axisFields.cells ?? {}) };
  const cell = cells[lvl] ?? {};
  cells[lvl] = {
    ...cell,
    referenceProfiles: [...(cell.referenceProfiles ?? []), { name: "", reason: "" }],
  };
  return { ...axisFields, cells };
}

/** Remove the reference profile at `idx` for a level. */
export function removeReferenceProfileIn(
  axisFields: StructuredRubricFields,
  level: number,
  idx: number,
): StructuredRubricFields {
  const lvl = String(level);
  const cell = axisFields.cells?.[lvl];
  if (!cell?.referenceProfiles) return axisFields;
  const cells = { ...(axisFields.cells ?? {}) };
  cells[lvl] = { ...cell, referenceProfiles: cell.referenceProfiles.filter((_, i) => i !== idx) };
  return { ...axisFields, cells };
}

/** Patch fields of the reference profile at `idx` for a level. */
export function patchReferenceProfileIn(
  axisFields: StructuredRubricFields,
  level: number,
  idx: number,
  patch: Partial<ReferenceProfile>,
): StructuredRubricFields {
  const lvl = String(level);
  const cell = axisFields.cells?.[lvl];
  if (!cell?.referenceProfiles) return axisFields;
  const cells = { ...(axisFields.cells ?? {}) };
  cells[lvl] = {
    ...cell,
    referenceProfiles: cell.referenceProfiles.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
  };
  return { ...axisFields, cells };
}
